import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ticketService } from '../../api/ticketService';
import { userManagementService } from '../../api/userManagementService';
import { useStatuses } from '../../hooks/useStatuses';
import { usePriorities } from '../../hooks/usePriorities';
import { useCategories } from '../../hooks/useCategories';
import { extractErrorMessage } from '../../utils/errorHandler';
import { excludeSuperAdminTickets } from '../../utils/reportFilters';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { formatDateTime } from '../../components/globaldeclaration';
import CountyFilterSelect, { countyFilterToId, regionFilterToId } from '../../components/reports/CountyFilterSelect';
import { downloadStyledXlsx } from '../../utils/exportStyledXlsx';
import { 
  Ticket, 
  Search, 
  Filter, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Calendar,
  User,
  X,
  Download,
  ChevronDown,
  FileText,
  BarChart3
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const ListOfTicketsReport = () => {
  const navigate = useNavigate();
  const [allTickets, setAllTickets] = useState([]); // Store all fetched tickets
  const [filteredTickets, setFilteredTickets] = useState([]); // Filtered tickets for display
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCounty, setSelectedCounty] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Dropdown data
  const [agents, setAgents] = useState([]);
  const { data: statuses = [], isLoading: loadingStatuses } = useStatuses();
  const { data: priorities = [], isLoading: loadingPriorities } = usePriorities();
  const { data: categories = [], isLoading: loadingCategories } = useCategories();
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [chartDataLoading, setChartDataLoading] = useState(false);
  const [reportStats, setReportStats] = useState({ totalTickets: 0, byStatus: [], byPriority: [] });
  const loadTicketsRef = useRef(null);
  const loadReportStatsRef = useRef(null);
  const setChartDataLoadingRef = useRef(null);
  const ticketsFetchIdRef = useRef(0);
  const statsFetchIdRef = useRef(0);

  // Load agents on mount (master data via React Query hooks)
  useEffect(() => {
    loadAgents();
  }, []);

  // Debounce search term to avoid too many API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load tickets when filters or pagination changes
  useEffect(() => {
    loadTickets();
  }, [currentPage, pageSize, debouncedSearchTerm, selectedAgent, selectedStatus, selectedPriority, selectedCategory, selectedSource, selectedRegion, selectedCounty, fromDate, toDate]);

  const buildReportFilterParams = () => {
    const params = {};
    if (selectedStatus !== 'all') {
      const selectedStatusObj = statuses.find(s => {
        const statusValue = s.statusCode || s.code || String(s.statusId || s.id);
        return statusValue === selectedStatus;
      });
      const statusCode = selectedStatusObj?.statusCode || selectedStatusObj?.code || selectedStatus;
      params.status = statusCode;
    }
    if (selectedPriority !== 'all') {
      const priorityId = parseInt(selectedPriority);
      if (!isNaN(priorityId)) params.priority = priorityId;
    }
    if (selectedCategory !== 'all') {
      const categoryId = parseInt(selectedCategory);
      if (!isNaN(categoryId)) params.categoryId = categoryId;
    }
    if (selectedAgent !== 'all') {
      const agentId = parseInt(selectedAgent);
      if (!isNaN(agentId)) params.assigneeId = agentId;
    }
    if (selectedSource !== 'all') params.source = selectedSource;
    const regionId = regionFilterToId(selectedRegion);
    if (regionId !== null) params.regionId = regionId;
    const countyId = countyFilterToId(selectedCounty);
    if (countyId !== null) params.countyId = countyId;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    const trimmedSearch = debouncedSearchTerm.trim();
    if (trimmedSearch) params.search = trimmedSearch;
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === undefined || params[key] === '') delete params[key];
    });
    return params;
  };

  const loadReportStats = async () => {
    const fetchId = ++statsFetchIdRef.current;
    try {
      setChartDataLoading(true);
      const response = await ticketService.getListTicketsReportStatistics(buildReportFilterParams());
      if (fetchId !== statsFetchIdRef.current) return;
      const data = response?.data?.data || response?.data || {};
      setReportStats({
        totalTickets: Number(data.totalTickets) || 0,
        byStatus: Array.isArray(data.byStatus) ? data.byStatus : [],
        byPriority: Array.isArray(data.byPriority) ? data.byPriority : []
      });
    } catch (err) {
      if (fetchId !== statsFetchIdRef.current) return;
      console.warn('Report statistics load failed:', err);
      setReportStats({ totalTickets: 0, byStatus: [], byPriority: [] });
    } finally {
      if (fetchId === statsFetchIdRef.current) {
        setChartDataLoading(false);
      }
    }
  };

  // Statistics for charts/summary — full filtered dataset (not the current page)
  useEffect(() => {
    loadReportStats();
  }, [debouncedSearchTerm, selectedAgent, selectedStatus, selectedPriority, selectedCategory, selectedSource, selectedRegion, selectedCounty, fromDate, toDate, statuses]);

  const loadAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await userManagementService.getAgents({ size: 1000 }); // Get all agents
      const agentsData = response?.data?.data?.content || response?.data?.data || response?.data || [];
      const agentsArray = Array.isArray(agentsData) ? agentsData : [];
      setAgents(agentsArray);
    } catch (error) {
      console.warn('Failed to load agents:', error);
      setAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadTickets = async () => {
    const fetchId = ++ticketsFetchIdRef.current;
    try {
      setLoading(true);
      setError('');
      
      // Build API params matching the endpoint: /gateway/api/ticket/list
      const params = {
        page: currentPage,
        size: pageSize
      };

      // Add status filter - send to API (server-side filtering)
      if (selectedStatus !== 'all') {
        // Get status code from selected status
        const selectedStatusObj = statuses.find(s => {
          const statusValue = s.statusCode || s.code || String(s.statusId || s.id);
          return statusValue === selectedStatus;
        });
        
        // Use statusCode if available, otherwise use the selected value
        const statusCode = selectedStatusObj?.statusCode || 
                          selectedStatusObj?.code || 
                          selectedStatus;
        params.status = statusCode;
      }

      // Add priority filter - send priorityId (number) to API
      if (selectedPriority !== 'all') {
        // selectedPriority is already priorityId from the dropdown
        const priorityId = parseInt(selectedPriority);
        if (!isNaN(priorityId)) {
          // API expects priority parameter with priorityId value
          params.priority = priorityId;
        }
      }

      // Add category filter
      if (selectedCategory !== 'all') {
        const categoryId = parseInt(selectedCategory);
        if (!isNaN(categoryId)) {
          params.categoryId = categoryId;
        }
      }

      // Add agent/assignee filter
      if (selectedAgent !== 'all') {
        const agentId = parseInt(selectedAgent);
        if (!isNaN(agentId)) {
          params.assigneeId = agentId;
        }
      }

      // Add source filter
      if (selectedSource !== 'all') {
        params.source = selectedSource;
      }

      // Add region / county filters
      const regionId = regionFilterToId(selectedRegion);
      if (regionId !== null) {
        params.regionId = regionId;
      }
      const countyId = countyFilterToId(selectedCounty);
      if (countyId !== null) {
        params.countyId = countyId;
      }

      // Add date range filters
      if (fromDate) {
        params.fromDate = fromDate;
      }
      if (toDate) {
        params.toDate = toDate;
      }

      const trimmedSearch = debouncedSearchTerm.trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      // Remove null/undefined/empty params
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      // Call the API with all filters
      const response = await ticketService.getAllTickets(params);
      if (fetchId !== ticketsFetchIdRef.current) return;
      
      const responseData = response?.data;
      
      let ticketsList = [];
      let total = 0;
      let pages = 0;
      
      if (responseData?.success && responseData?.data) {
        const paginatedData = responseData.data;
        
        // Handle paginated response structure
        if (paginatedData.content) {
          ticketsList = paginatedData.content;
          total = paginatedData.totalElements || 0;
          pages = paginatedData.totalPages || 0;
        } else if (Array.isArray(paginatedData)) {
          ticketsList = paginatedData;
          total = paginatedData.length;
          pages = 1;
        }
      } else if (Array.isArray(responseData)) {
        ticketsList = responseData;
        total = responseData.length;
        pages = 1;
      }
      
      const visibleTickets = excludeSuperAdminTickets(ticketsList);

      // Store fetched tickets
      setAllTickets(visibleTickets);
      setFilteredTickets(visibleTickets);

      // Store API pagination data
      setPaginationData({
        totalElements: total,
        totalPages: pages,
        pageNumber: currentPage,
        pageSize: pageSize,
        first: currentPage === 0,
        last: currentPage >= pages - 1
      });
    } catch (error) {
      if (fetchId !== ticketsFetchIdRef.current) return;
      console.error('Error loading tickets:', error);
      setError(extractErrorMessage(error, 'Failed to load tickets'));
      toast.error(extractErrorMessage(error, 'Failed to load tickets'));
      setAllTickets([]);
      setFilteredTickets([]);
      setPaginationData(null);
    } finally {
      if (fetchId === ticketsFetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  loadTicketsRef.current = loadTickets;
  loadReportStatsRef.current = loadReportStats;
  setChartDataLoadingRef.current = setChartDataLoading;

  useEffect(() => {
    const onRealtimeRefresh = () => {
      setChartDataLoadingRef.current?.(true);
      loadTicketsRef.current?.();
      loadReportStatsRef.current?.();
    };
    window.addEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
    return () => window.removeEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
  }, []);


  // Charts from dedicated statistics API (full filtered dataset)
  const pieChartData = useMemo(() => {
    const entries = (reportStats.byStatus || [])
      .map((item) => ({
        name: String(item.name || 'Unknown').trim() || 'Unknown',
        value: Number(item.count) || 0
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
    const total = entries.reduce((sum, item) => sum + item.value, 0);
    const threshold = Math.max(1, Math.floor(total * 0.02));
    const main = [];
    let otherCount = 0;
    entries.forEach(({ name, value }) => {
      if (value >= threshold) {
        main.push({ name, value });
      } else {
        otherCount += value;
      }
    });
    if (otherCount > 0) {
      main.push({ name: 'Other', value: otherCount });
    }
    return main;
  }, [reportStats]);

  const barChartData = useMemo(() => {
    return (reportStats.byPriority || [])
      .map((item) => ({
        name: String(item.name || 'Unknown').trim() || 'Unknown',
        count: Number(item.count) || 0
      }))
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [reportStats]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && (paginationData ? newPage < paginationData.totalPages : true)) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedAgent('all');
    setSelectedStatus('all');
    setSelectedPriority('all');
    setSelectedCategory('all');
    setSelectedSource('all');
    setSelectedRegion('all');
    setSelectedCounty('all');
    setFromDate('');
    setToDate('');
    setCurrentPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getPriorityBadgeColor = (priority) => {
    const priorityLower = (priority || '').toLowerCase();
    if (priorityLower.includes('critical')) return 'bg-red-100 text-red-800 border-red-300';
    if (priorityLower.includes('high')) return 'bg-orange-100 text-orange-800 border-orange-300';
    if (priorityLower.includes('medium')) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (priorityLower.includes('low')) return 'bg-green-100 text-green-800 border-green-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusBadgeColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'open') return 'bg-blue-100 text-blue-800 border-blue-300';
    if (statusLower === 'in progress' || statusLower === 'in-progress') return 'bg-purple-100 text-purple-800 border-purple-300';
    if (statusLower === 'resolved') return 'bg-green-100 text-green-800 border-green-300';
    if (statusLower === 'closed') return 'bg-gray-100 text-gray-800 border-gray-300';
    if (statusLower === 'escalated') return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getTicketDueDate = (ticket) =>
    ticket?.dueDate || ticket?.slaDueDate || ticket?.sla?.dueDate || null;

  const getAgentName = (ticket) => {
    // First, check if ticket has assignee name directly
    if (ticket.assigneeName) {
      return ticket.assigneeName;
    }
    
    if (ticket.assignedAgentName) {
      return ticket.assignedAgentName;
    }
    
    // Check if ticket has assignee object with name
    if (ticket.assignee) {
      const name = ticket.assignee.fullName || ticket.assignee.name || ticket.assignee.username || ticket.assignee.userName;
      if (name) return name;
    }
    
    if (ticket.assignedAgent) {
      const name = ticket.assignedAgent.fullName || ticket.assignedAgent.name || ticket.assignedAgent.username || ticket.assignedAgent.userName;
      if (name) return name;
    }
    
    // Check multiple possible field names for assignee ID
    const assigneeId = ticket.assigneeId || ticket.assignedAgentId || ticket.assignedToId || ticket.assignedTo;
    
    if (assigneeId) {
      // Try to find agent by multiple ID fields (handle both number and string comparisons)
      const agent = agents.find(a => {
        const agentId = a.userId || a.id;
        return agentId === assigneeId || 
               String(agentId) === String(assigneeId) ||
               Number(agentId) === Number(assigneeId);
      });
      
      if (agent) {
        return agent.fullName || agent.name || agent.username || agent.userName || 'Unknown';
      }
      
      // If agent not found, return Unassigned
      return 'Unassigned';
    }
    
    return 'Unassigned';
  };

  const hasActiveFilters = searchTerm || selectedAgent !== 'all' || selectedStatus !== 'all' || selectedPriority !== 'all' || selectedCategory !== 'all' || selectedSource !== 'all' || selectedRegion !== 'all' || selectedCounty !== 'all' || fromDate || toDate;

  const buildExportParams = (page, size) => {
    const params = { page, size };
    if (selectedStatus !== 'all') {
      const selectedStatusObj = statuses.find(s => (s.statusCode || s.code || String(s.statusId || s.id)) === selectedStatus);
      params.status = selectedStatusObj?.statusCode || selectedStatusObj?.code || selectedStatus;
    }
    if (selectedPriority !== 'all') {
      const priorityId = parseInt(selectedPriority);
      if (!isNaN(priorityId)) params.priority = priorityId;
    }
    if (selectedCategory !== 'all') {
      const categoryId = parseInt(selectedCategory);
      if (!isNaN(categoryId)) params.categoryId = categoryId;
    }
    if (selectedAgent !== 'all') {
      const agentId = parseInt(selectedAgent);
      if (!isNaN(agentId)) params.assigneeId = agentId;
    }
    if (selectedSource !== 'all') params.source = selectedSource;
    const regionId = regionFilterToId(selectedRegion);
    if (regionId !== null) params.regionId = regionId;
    const countyId = countyFilterToId(selectedCounty);
    if (countyId !== null) params.countyId = countyId;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (debouncedSearchTerm) params.search = debouncedSearchTerm;
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === undefined || params[key] === '') delete params[key];
    });
    return params;
  };

  const fetchAllTicketsForExport = async () => {
    const first = await ticketService.getAllTickets(buildExportParams(0, 10));
    const responseData = first?.data;
    let total = 0;
    if (responseData?.success && responseData?.data) {
      const pd = responseData.data;
      if (pd.content) total = pd.totalElements || 0;
      else if (Array.isArray(pd)) total = pd.length;
    } else if (Array.isArray(responseData)) total = responseData.length;
    if (total <= 0) return [];
    const full = await ticketService.getAllTickets(buildExportParams(0, total));
    const fd = full?.data;
    if (fd?.success && fd?.data?.content) return fd.data.content;
    if (fd?.success && Array.isArray(fd?.data)) return fd.data;
    if (Array.isArray(fd)) return fd;
    return [];
  };

  const handleExportExcel = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const allTickets = await fetchAllTicketsForExport();
      if (allTickets.length === 0) {
        toast.error('No data to export');
        return;
      }
      const headers = [
        'S.No',
        'Ticket ID',
        'Ticket Title',
        'Category',
        'Priority',
        'Source',
        'Agent Name',
        'Status',
        'Created Date & Time',
        'Resolved Date & Time',
        'Closed Date & Time',
        'Due Date',
      ];
      const rows = allTickets.map((ticket, i) => {
        const cat = ticket.category?.categoryName || ticket.categoryName || ticket.category || 'N/A';
        const pri = ticket.priority?.priorityName || ticket.priorityName || ticket.priority || 'N/A';
        const st = ticket.status?.statusName || ticket.statusName || ticket.status || 'N/A';
        const agent = ticket.assigneeFullName || getAgentName(ticket) || 'unassigned';
        const dueDate = getTicketDueDate(ticket);
        return [
          i + 1,
          ticket.ticketNumber || ticket.ticketId || 'N/A',
          ticket.subject || ticket.title || 'N/A',
          cat,
          pri,
          ticket.source || 'Portal',
          agent,
          st,
          // Raw timestamps for dateColumns; empty / N/A stay text via exportStyledXlsx
          ticket.createdAt || ticket.createdDate || '',
          ticket.resolvedAt || '',
          ticket.closedAt || '',
          dueDate || 'N/A',
        ];
      });
      downloadStyledXlsx({
        fileName: `list-of-tickets-report-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: 'List of Tickets Report',
        headers,
        rows,
        numberColumns: [0],
        textColumns: [1, 2, 3, 4, 5, 6, 7],
        dateColumns: [8, 9, 10, 11],
        centerColumns: [0, 4, 5, 7, 8, 9, 10, 11],
      });
      toast.success('Report exported as Excel');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const allTickets = await fetchAllTicketsForExport();
      if (allTickets.length === 0) {
        toast.error('No data to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('List of Tickets Report', 14, 15);
      doc.setFontSize(10);
      const body = allTickets.map((ticket, i) => {
        const cat = ticket.category?.categoryName || ticket.categoryName || ticket.category || 'N/A';
        const pri = ticket.priority?.priorityName || ticket.priorityName || ticket.priority || 'N/A';
        const st = ticket.status?.statusName || ticket.statusName || ticket.status || 'N/A';
        const agent = ticket.assigneeFullName || getAgentName(ticket) || 'unassigned';
        const dueDate = getTicketDueDate(ticket);
        return [i + 1, ticket.ticketNumber || ticket.ticketId || 'N/A', (ticket.subject || ticket.title || 'N/A').substring(0, 30), cat.substring(0, 15), pri, ticket.source || 'Portal', agent.substring(0, 15), st, formatDateTime(ticket.createdAt || ticket.createdDate), formatDateTime(ticket.resolvedAt || ''), formatDateTime(ticket.closedAt || ''), dueDate ? formatDateTime(dueDate) : 'N/A'];
      });
      autoTable(doc, {
        startY: 22,
        head: [['S.No', 'Ticket ID', 'Title', 'Category', 'Priority', 'Source', 'Agent', 'Status', 'Created', 'Resolved', 'Closed', 'Due']],
        body,
        styles: { fontSize: 6 }
      });
      doc.save(`list-of-tickets-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Report exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">List of Tickets Report</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View and filter all tickets with detailed information
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowFilters((prev) => !prev);
              setExportMenuOpen(false);
            }}
            className="flex items-center gap-2"
            aria-expanded={showFilters}
          >
            <Filter className="h-4 w-4" />
            Filter
            {hasActiveFilters && !showFilters && (
              <span className="ml-1 h-2 w-2 rounded-full bg-primary" aria-hidden />
            )}
          </Button>
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportMenuOpen((v) => !v)}
              disabled={loading || exporting}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exporting…' : 'Export'}
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 min-w-[140px]">
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportExcel}>
                  <FileText className="h-4 w-4" /> Export Excel
                </button>
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportPdf}>
                  <FileText className="h-4 w-4" /> Export PDF
                </button>
              </div>
            )}
          </div>
          <Button
            onClick={() => {
              loadTickets();
              loadReportStats();
            }}
            disabled={loading}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters Card (toggle via Filter button) */}
      {showFilters && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* All Filters in Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search Filter - Reduced size */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by ticket ID, title..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(0);
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            {/* From Date Filter - Moved after search */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                From Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={fromDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    // Validate: don't allow future dates
                    const today = new Date().toISOString().split('T')[0];
                    if (selectedDate > today) {
                      toast.error('From Date cannot be in the future');
                      return;
                    }
                    // Validate: fromDate should not be after toDate
                    if (toDate && selectedDate > toDate) {
                      toast.error('From Date cannot be after To Date');
                      return;
                    }
                    setFromDate(selectedDate);
                    setCurrentPage(0);
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            {/* To Date Filter - Moved after search */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                To Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    // Validate: don't allow future dates
                    const today = new Date().toISOString().split('T')[0];
                    if (selectedDate > today) {
                      toast.error('To Date cannot be in the future');
                      return;
                    }
                    // Validate: toDate should not be before fromDate
                    if (fromDate && selectedDate < fromDate) {
                      toast.error('To Date cannot be before From Date');
                      return;
                    }
                    setToDate(selectedDate);
                    setCurrentPage(0);
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Agent Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Agent
              </label>
              <Select
                value={selectedAgent}
                onValueChange={(value) => {
                  setSelectedAgent(value);
                  setCurrentPage(0);
                }}
                disabled={loadingAgents}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Agents" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {agents.map((agent) => (
                    <SelectItem
                      key={agent.userId || agent.id}
                      value={String(agent.userId || agent.id)}
                    >
                      {agent.fullName || agent.name || agent.username || 'Unknown'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <Select
                value={selectedStatus}
                onValueChange={(value) => {
                  setSelectedStatus(value);
                  setCurrentPage(0);
                }}
                disabled={loadingStatuses}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {statuses.map((status) => {
                    // Backend expects statusCode
                    let statusValue = status.statusCode || status.code;
                    
                    if (!statusValue && status.statusName) {
                      statusValue = status.statusName.toLowerCase().replace(/\s+/g, '-');
                    }
                    
                    if (!statusValue) {
                      statusValue = String(status.statusId || status.id);
                    }
                    
                    const statusDisplay = status.statusName || status.name || 'Unknown';
                    return (
                      <SelectItem
                        key={status.statusId || status.id}
                        value={statusValue}
                      >
                        {statusDisplay}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Priority
              </label>
              <Select
                value={selectedPriority}
                onValueChange={(value) => {
                  setSelectedPriority(value);
                  setCurrentPage(0);
                }}
                disabled={loadingPriorities}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  {priorities.map((priority) => {
                    // Use priorityId as the value (number)
                    const priorityId = priority.priorityId || priority.id;
                    const priorityValue = priorityId ? String(priorityId) : 'all';
                    const priorityDisplay = priority.priorityName || priority.name || 'Unknown';
                    
                    return (
                      <SelectItem
                        key={priorityId || `priority-${priority.priorityName}`}
                        value={priorityValue}
                      >
                        {priorityDisplay}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Category
              </label>
              <Select
                value={selectedCategory}
                onValueChange={(value) => {
                  setSelectedCategory(value);
                  setCurrentPage(0);
                }}
                disabled={loadingCategories}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem
                      key={category.categoryId || category.id}
                      value={String(category.categoryId || category.id)}
                    >
                      {category.categoryName || category.name || 'Unknown'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Source
              </label>
              <Select
                value={selectedSource}
                onValueChange={(value) => {
                  setSelectedSource(value);
                  setCurrentPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="portal">Portal</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">whatsapp</SelectItem>
                  <SelectItem value="chatbot">Chatbot</SelectItem>
                  {/* <SelectItem value="api">API</SelectItem> */}
                </SelectContent>
              </Select>
            </div>

            {/* Region / County Filter */}
            <div className="lg:col-span-2">
              <CountyFilterSelect
                regionValue={selectedRegion}
                countyValue={selectedCounty}
                onRegionChange={(value) => {
                  setSelectedRegion(value);
                  setCurrentPage(0);
                }}
                onCountyChange={(value) => {
                  setSelectedCounty(value);
                  setCurrentPage(0);
                }}
              />
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <div className="lg:col-span-4 flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      )}

      {/* Charts: Pie (by status) and Bar (by priority) */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                Tickets by status
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Distribution of tickets by status (filtered list)
              </p>
            </CardHeader>
            <CardContent>
              {chartDataLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <RefreshCw className="h-8 w-8 animate-spin mb-3" />
                  <p className="text-sm">Loading chart...</p>
                </div>
              ) : pieChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display. Apply filters and ensure tickets are loaded.</p>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="45%"
                      innerRadius={56}
                      outerRadius={88}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [value, name]}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      labelFormatter={(name) => name}
                    />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      wrapperStyle={{ fontSize: '12px' }}
                      formatter={(value, entry) => (
                        <span className="text-foreground">
                          {value} {entry.payload?.value != null ? `(${entry.payload.value})` : ''}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                Tickets by priority
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Ticket count by priority (filtered list)
              </p>
            </CardHeader>
            <CardContent>
              {chartDataLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <RefreshCw className="h-8 w-8 animate-spin mb-3" />
                  <p className="text-sm">Loading chart...</p>
                </div>
              ) : barChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display. Apply filters and ensure tickets are loaded.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={barChartData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="name" className="text-xs" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} className="text-xs" />
                    <Tooltip
                      formatter={(value) => [value, 'Count']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill={CHART_COLORS[0]} name="Tickets" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tickets Table Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5" />
            Tickets
            {paginationData && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({paginationData.totalElements} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading tickets...</span>
            </div>
           ) : filteredTickets.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No tickets found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full table-fixed border-collapse min-w-[1100px]">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[4%]">S.No</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[9%]">Ticket ID</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[18%]">Ticket Title</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[8%]">Category</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[7%]">Priority</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[7%]">Source</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[9%]">Agent Name</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[7%]">Status</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[10%]">Created Date & Time</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[10%]">Resolved Date & Time</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[10%]">Closed Date & Time</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground w-[9%]">Due Date</th>
                    </tr>
                  </thead>
                   <tbody>
                     {filteredTickets.map((ticket, index) => (
                      <tr
                        key={ticket.ticketId || ticket.id}
                        className="border-b border-border hover:bg-muted/30 transition-colors cursor-pointer"
                        
                      >
                        <td className="p-3 text-sm text-foreground">
                          {currentPage * pageSize + index + 1}
                        </td>
                        <td className="p-3 text-sm text-foreground align-top whitespace-nowrap">
                          #{ticket.ticketNumber || ticket.ticketId || ticket.id}
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <p
                            className="font-medium break-all line-clamp-3 leading-snug"
                            title={ticket.subject || ticket.title || 'N/A'}
                          >
                            {ticket.subject || ticket.title || 'N/A'}
                          </p>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <span
                            className="line-clamp-2 break-words"
                            title={ticket.category?.categoryName || ticket.categoryName || ticket.category || 'N/A'}
                          >
                            {ticket.category?.categoryName || ticket.categoryName || ticket.category || 'N/A'}
                          </span>
                        </td>
                        <td className="p-3">
                          <Badge
                            className={`${getPriorityBadgeColor(
                              ticket.priority?.priorityName || ticket.priorityName || ticket.priority
                            )} border`}
                          >
                            {ticket.priority?.priorityName || ticket.priorityName || ticket.priority || 'N/A'}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm text-foreground capitalize">
                          {ticket.source || 'Portal'}
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <span className="line-clamp-2 break-words" title={ticket.assigneeFullName || 'unassigned'}>
                            {ticket.assigneeFullName || 'unassigned'}
                          </span>
                        </td>
                        <td className="p-3">
                          <Badge
                            className={`${getStatusBadgeColor(
                              ticket.status?.statusName || ticket.statusName || ticket.status
                            )} border`}
                          >
                            {ticket.status?.statusName || ticket.statusName || ticket.status || 'N/A'}
                          </Badge>
                        </td> 
                        <td className="p-3 text-sm text-foreground">
                          {formatDateTime(ticket.createdAt || ticket.createdDate)}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {formatDateTime(ticket.resolvedAt || '')}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {formatDateTime(ticket.closedAt || '')}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {(() => {
                            const dueDate = getTicketDueDate(ticket);
                            return dueDate ? formatDateTime(dueDate) : 'N/A';
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {paginationData && paginationData.totalElements > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t border-border gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {currentPage * pageSize + 1} to{' '}
                    {Math.min((currentPage + 1) * pageSize, paginationData.totalElements)} of{' '}
                    {paginationData.totalElements} tickets
                  </div>
                  <div className="flex items-center gap-2">
                    {/* First Page Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(0)}
                        disabled={currentPage === 0 || loading}
                        className="hidden sm:flex"
                      >
                        First
                      </Button>
                    )}
                    
                    {/* Previous Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0 || loading}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                    )}
                    
                    {/* Page Numbers */}
                    {paginationData.totalPages > 0 && (
                      <div className="flex items-center gap-1">
                        {(() => {
                          const pages = [];
                          const totalPages = paginationData.totalPages;
                          const current = currentPage + 1;
                          
                          // Show page numbers with ellipsis
                          if (totalPages <= 7) {
                            // Show all pages if 7 or fewer
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(
                                <Button
                                  key={i}
                                  variant={i === current ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => handlePageChange(i - 1)}
                                  disabled={loading || totalPages === 1}
                                  className="min-w-[2.5rem]"
                                >
                                  {i}
                                </Button>
                              );
                            }
                          } else {
                            // Show first page
                            pages.push(
                              <Button
                                key={1}
                                variant={current === 1 ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(0)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                1
                              </Button>
                            );
                            
                            // Show ellipsis if current page is far from start
                            if (current > 3) {
                              pages.push(
                                <span key="ellipsis-start" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }
                            
                            // Show pages around current page
                            const start = Math.max(2, current - 1);
                            const end = Math.min(totalPages - 1, current + 1);
                            
                            for (let i = start; i <= end; i++) {
                              if (i !== 1 && i !== totalPages) {
                                pages.push(
                                  <Button
                                    key={i}
                                    variant={i === current ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => handlePageChange(i - 1)}
                                    disabled={loading}
                                    className="min-w-[2.5rem]"
                                  >
                                    {i}
                                  </Button>
                                );
                              }
                            }
                            
                            // Show ellipsis if current page is far from end
                            if (current < totalPages - 2) {
                              pages.push(
                                <span key="ellipsis-end" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }
                            
                            // Show last page
                            pages.push(
                              <Button
                                key={totalPages}
                                variant={current === totalPages ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(totalPages - 1)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                {totalPages}
                              </Button>
                            );
                          }
                          
                          return pages;
                        })()}
                      </div>
                    )}
                    
                    {/* Next Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    )}
                    
                    {/* Last Page Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(paginationData.totalPages - 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                        className="hidden sm:flex"
                      >
                        Last
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ListOfTicketsReport;

